import { fmtDate } from '@jt/core';
import type { FileItem } from '@jt/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState, type FormEvent } from 'react';
import { ApiError, del, patch, post } from '../api/client';
import { keys, useFiles } from '../api/hooks';
import { TextField } from '../components/fields';
import { ConfirmButton, useUI } from '../components/overlays';
import { Icon, PageHead } from '../components/ui';
import { fileHref, uploadFile } from '../panel/Files';

const kindLabel = (f: FileItem) =>
  !f.stored ? 'Ссылка' : f.mime === 'application/pdf' ? 'PDF' : f.mime.includes('word') ? 'DOCX' : 'Файл';

/** Просмотр PDF почти во весь экран: по ширине страницы, на весь экран или в новой вкладке браузера. */
function PdfPreview({ f }: { f: FileItem }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const canFull = typeof document !== 'undefined' && !!document.fullscreenEnabled;
  return (
    <>
      <div className="btnrow pdfbar">
        <a className="btn sm" href={fileHref(f, true)} target="_blank" rel="noopener noreferrer">
          <Icon name="ext" size={15} /> Открыть в новой вкладке
        </a>
        {canFull && (
          <button
            className="btn sm"
            type="button"
            onClick={() => void frame.current?.requestFullscreen().catch(() => undefined)}
          >
            <Icon name="eye" size={15} /> На весь экран
          </button>
        )}
        <a className="btn sm" href={fileHref(f)}>
          <Icon name="download" size={15} /> Скачать
        </a>
      </div>
      {/* #view=FitH — страница по ширине окна, а не мелким листом */}
      <iframe
        ref={frame}
        className="pdfview"
        title={`Просмотр: ${f.name}`}
        src={`${fileHref(f, true)}#view=FitH`}
        allowFullScreen
      />
      <p className="muted sm pdfnote">
        На телефоне браузер может не показать PDF внутри страницы — тогда «Открыть в новой вкладке».
      </p>
    </>
  );
}

function openPreview(ui: ReturnType<typeof useUI>, f: FileItem) {
  ui.openSheet({ title: f.name, wide: true, body: <PdfPreview f={f} /> });
}

function ResumeRow({ f }: { f: FileItem }) {
  const ui = useUI();
  const qc = useQueryClient();
  const save = async (fields: Partial<Pick<FileItem, 'lang' | 'note' | 'name'>>) => {
    const cur = qc.getQueryData<FileItem[]>(keys.files('resume'))?.find((x) => x.id === f.id);
    await patch(`/files/${f.id}`, { ...fields, version: cur?.version ?? f.version });
    await qc.invalidateQueries({ queryKey: ['files'] });
  };
  return (
    <div className="lrow">
      <span className="tic">
        <Icon name="file" size={20} />
      </span>
      <div className="lm">
        <b>{f.name}</b>
        <div className="meta">
          {kindLabel(f)} · добавлено {fmtDate(f.createdAt.slice(0, 10))} · отправлено: {f.usedCount}
        </div>
        <div className="grid" style={{ marginTop: 8 }}>
          <TextField
            label="Язык"
            name="lang"
            value={f.lang}
            placeholder="EN, ES, RU"
            onSave={(v) => save({ lang: v ?? '' })}
          />
          <TextField
            label="Для каких ролей"
            name="note"
            value={f.note}
            placeholder="Manual + automation"
            onSave={(v) => save({ note: v ?? '' })}
          />
        </div>
        <div className="btnrow">
          {f.stored && f.mime === 'application/pdf' && (
            <button className="btn sm" onClick={() => openPreview(ui, f)}>
              <Icon name="eye" size={15} /> Просмотр
            </button>
          )}
          <a className="btn sm" href={fileHref(f)} target="_blank" rel="noopener noreferrer">
            <Icon name={f.stored ? 'download' : 'ext'} size={15} /> {f.stored ? 'Скачать' : 'Открыть'}
          </a>
          <ConfirmButton
            className="btn sm ghost danger"
            confirmText="Точно удалить?"
            onConfirm={async () => {
              await del(`/files/${f.id}`);
              await qc.invalidateQueries({ queryKey: ['files'] });
              ui.toast('Удалено');
            }}
          >
            Удалить
          </ConfirmButton>
        </div>
      </div>
    </div>
  );
}

export function ResumesPage() {
  const ui = useUI();
  const qc = useQueryClient();
  const list = useFiles('resume');
  const input = useRef<HTMLInputElement>(null);
  const [err, setErr] = useState('');

  const onUpload = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return ui.toast('Файл больше 20 МБ.');
    ui.toast('Загружаю…');
    try {
      await uploadFile(file, 'resume');
      await qc.invalidateQueries({ queryKey: ['files'] });
      ui.toast('Резюме загружено');
    } catch (e) {
      ui.toast(e instanceof ApiError ? e.message : 'Не загрузилось');
    }
  };

  const addLink = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const url = String(fd.get('url') ?? '').trim();
    if (!url) return setErr('Вставьте ссылку.');
    try {
      await post('/files/link', { name: String(fd.get('name') ?? '').trim(), url, kind: 'resume' });
      form.reset();
      setErr('');
      await qc.invalidateQueries({ queryKey: ['files'] });
      ui.toast('Резюме добавлено');
    } catch (x) {
      setErr(x instanceof ApiError ? (Object.values(x.fields)[0] ?? x.message) : 'Не сохранилось');
    }
  };

  return (
    <>
      <PageHead title="Резюме" sub="Версии CV, которые вы отправляете">
        <button className="btn primary" onClick={() => input.current?.click()}>
          <Icon name="upload" /> Загрузить PDF или DOCX
        </button>
        <input
          ref={input}
          type="file"
          accept=".pdf,.docx,.doc,.odt"
          hidden
          onChange={(e) => {
            void onUpload(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </PageHead>
      <section className="box">
        <h2>Мои версии</h2>
        {list.data?.length ? (
          <div className="list">
            {list.data.map((f) => (
              <ResumeRow key={f.id} f={f} />
            ))}
          </div>
        ) : (
          <p className="muted" style={{ margin: '0 0 4px' }}>
            Пока пусто. Загрузите PDF или DOCX, или добавьте ссылку — потом прикрепляйте нужную версию к
            откликам.
          </p>
        )}
      </section>
      <section className="box">
        <h2>Добавить по ссылке</h2>
        <p className="muted sm" style={{ marginTop: -6 }}>
          Например, на Google Docs или Google Drive.
        </p>
        <form onSubmit={addLink}>
          <div className="grid">
            <label className="f">
              Название
              <input name="name" placeholder="Senior_QA_EN.pdf" />
            </label>
            <label className="f">
              Ссылка
              <input name="url" type="url" placeholder="https://" />
            </label>
          </div>
          {err && <p className="fnote err">{err}</p>}
          <div className="btnrow" style={{ marginTop: 10 }}>
            <button className="btn" type="submit">
              <Icon name="link" /> Добавить
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
